import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    
    // Add scroll listener for carousel
    const slideshowComponent = this.querySelector('slideshow-component');
    console.log('ZoomDialog connectedCallback - slideshowComponent:', slideshowComponent);
    if (slideshowComponent && 'refs' in slideshowComponent) {
      console.log('ZoomDialog connectedCallback - slideshowComponent.refs:', slideshowComponent.refs);
      const scroller = slideshowComponent.querySelector('slideshow-slides');
      if (scroller) {
        scroller.addEventListener('scroll', this.handleCarouselScroll.bind(this));
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, thumbnails } = this.refs;
    
    // Get media elements from the carousel structure
    const slideshowSlides = this.querySelector('slideshow-slides');
    const media = slideshowSlides ? Array.from(slideshowSlides.children) : [];
    
    // Validate index
    if (index < 0 || index >= media.length) {
      console.warn('Invalid media index:', index);
      return;
    }
    
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      // Wait for the dialog to be fully rendered before initializing slideshow
      requestAnimationFrame(() => {
        // Go to the specific slide in carousel instead of scrolling
        this.goToSlide(index);
      });
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions || !sourceImage || !targetImage) return open();

    const transitionName = `gallery-item`;
    if (sourceImage instanceof HTMLElement) {
      sourceImage.style.setProperty('view-transition-name', transitionName);
    }

    await startViewTransition(() => {
      open();
      if (sourceImage instanceof HTMLElement) {
        sourceImage.style.removeProperty('view-transition-name');
      }
      if (targetImage instanceof HTMLElement) {
        targetImage.style.setProperty('view-transition-name', transitionName);
      }
    });

    if (targetImage instanceof HTMLElement) {
      targetImage.style.removeProperty('view-transition-name');
    }
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { thumbnails } = this.refs;
    const media = this.getMediaElements();

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.#loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog } = this.refs;
    const media = this.getMediaElements();

    if (!supportsViewTransitions) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const media = this.getMediaElements();
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    // Get media elements from carousel structure
    const media = this.getMediaElements();
    const targetImage = media[index];

    if (targetImage) {
      this.#loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Gets the media elements from the carousel structure.
   * @returns {HTMLElement[]} Array of media elements.
   */
  getMediaElements() {
    const slideshowComponent = this.querySelector('slideshow-component');
    if (!slideshowComponent) return [];
    
    // Get scroller directly from slideshow component
    const scroller = slideshowComponent.querySelector('slideshow-slides');
    return scroller ? Array.from(scroller.children).filter(el => el instanceof HTMLElement) : [];
  }

  /**
   * Goes to the previous slide in the carousel.
   */
  previousSlide() {
    const slideshowComponent = this.querySelector('slideshow-component');
    if (slideshowComponent && 'previous' in slideshowComponent && typeof slideshowComponent.previous === 'function') {
      slideshowComponent.previous();
    }
  }

  /**
   * Goes to the next slide in the carousel.
   */
  nextSlide() {
    const slideshowComponent = this.querySelector('slideshow-component');
    if (slideshowComponent && 'next' in slideshowComponent && typeof slideshowComponent.next === 'function') {
      slideshowComponent.next();
    }
  }

  /**
   * Goes to a specific slide in the carousel.
   * @param {number} index - The index of the slide to go to.
   */
  goToSlide(index) {
    const slideshowComponent = this.querySelector('slideshow-component');
    
    
    if (slideshowComponent && 'select' in slideshowComponent && typeof slideshowComponent.select === 'function') {
      slideshowComponent.select(index);
      // Update thumbnails to stay in sync
      this.selectThumbnail(index, { behavior: 'auto' });
    } else {
      console.warn('Slideshow component not properly initialized or select method not available');
    }
  }

  /**
   * Gets the current slide index based on scroll position.
   * @returns {number} The current slide index.
   */
  getCurrentSlideIndex() {
    const slideshowComponent = this.querySelector('slideshow-component');
    if (slideshowComponent && 'current' in slideshowComponent && typeof slideshowComponent.current === 'number') {
      return slideshowComponent.current;
    }
    return 0;
  }


  /**
   * Handles carousel scroll events to update thumbnails.
   */
  handleCarouselScroll = debounce(() => {
    const currentIndex = this.getCurrentSlideIndex();
    this.selectThumbnail(currentIndex, { behavior: 'auto' });
  }, 50);
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
